import { useParams, Link, Navigate } from "react-router-dom";
import { useMemo } from "react";
import { blogPosts } from "../../../mocks/blog";
import BlogNavbar from "../components/BlogNavbar";
import ArticleHeader from "./components/ArticleHeader";
import ArticleBody from "./components/ArticleBody";
import RelatedPosts from "./components/RelatedPosts";
import Footer from "../../home/components/Footer";
import JsonLd from "../../../components/feature/JsonLd";
import { useSeoMeta } from "../../../hooks/useSeoMeta";
import { categoryToSlug } from "../../../utils/blogSlug";

const SITE_URL = import.meta.env.VITE_SITE_URL ?? "https://ltfsourcing.com";

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  const post = blogPosts.find((p) => p.slug === slug);

  useSeoMeta({
    title: post ? `${post.title} | LTF Sourcing Blog` : "Article | LTF Sourcing Blog",
    description: post?.excerpt ?? "Read expert insights on global product sourcing, wholesale supply, and inventory solutions on the LTF Sourcing Blog.",
    canonical: post ? `${SITE_URL}/blog/${post.slug}` : `${SITE_URL}/blog`,
    ogTitle: post?.title ?? "LTF Sourcing Blog",
    ogDescription: post?.excerpt ?? "",
    ogType: "article",
    ogImage: post?.coverImage,
    keywords: post?.tags.join(", "),
  });

  const related = useMemo(() => {
    if (!post) return [];
    return blogPosts
      .filter((p) => p.slug !== post.slug && p.category === post.category)
      .slice(0, 3)
      .concat(
        blogPosts
          .filter((p) => p.slug !== post.slug && p.category !== post.category)
          .slice(0, Math.max(0, 3 - blogPosts.filter((p) => p.slug !== post.slug && p.category === post.category).length))
      )
      .slice(0, 3);
  }, [post]);

  const articleSchema = useMemo(() => {
    if (!post) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: post.coverImage,
      datePublished: post.date,
      dateModified: post.date,
      author: {
        "@type": "Person",
        name: post.author.name,
        jobTitle: post.author.role,
        worksFor: { "@type": "Organization", name: "LTF Sourcing" },
      },
      publisher: {
        "@type": "Organization",
        name: "LTF Sourcing",
        url: SITE_URL,
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `${SITE_URL}/blog/${post.slug}`,
      },
      keywords: post.tags.join(", "),
      articleSection: post.category,
      timeRequired: `PT${post.readTime}M`,
      url: `${SITE_URL}/blog/${post.slug}`,
    };
  }, [post]);

  const faqSchema = useMemo(() => {
    if (!post?.faq || post.faq.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: post.faq.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    };
  }, [post]);

  const breadcrumbSchema = useMemo(() => {
    if (!post) return null;
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
        {
          "@type": "ListItem",
          position: 3,
          name: post.category,
          item: `${SITE_URL}/blog/category/${categoryToSlug(post.category)}`,
        },
        {
          "@type": "ListItem",
          position: 4,
          name: post.title,
          item: `${SITE_URL}/blog/${post.slug}`,
        },
      ],
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <main className="min-h-screen w-full bg-white">
      {articleSchema && <JsonLd id={`schema-article-${post.slug}`} schema={articleSchema} />}
      {faqSchema && <JsonLd id={`schema-faq-${post.slug}`} schema={faqSchema} />}
      {breadcrumbSchema && <JsonLd id={`schema-article-breadcrumb-${post.slug}`} schema={breadcrumbSchema} />}

      <BlogNavbar />

      {/* Top padding for fixed navbar */}
      <div className="pt-16">
        {/* Breadcrumb */}
        <div className="max-w-3xl mx-auto px-6 lg:px-0 pt-8">
          <nav className="flex items-center gap-2 text-xs text-[#3A506B]/60">
            <Link to="/" className="hover:text-[#C9A227] transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-sm"></i>
            <Link to="/blog" className="hover:text-[#C9A227] transition-colors cursor-pointer">Blog</Link>
            <i className="ri-arrow-right-s-line text-sm"></i>
            <span className="text-[#222831] font-medium line-clamp-1">{post.title}</span>
          </nav>
        </div>

        {/* Article Header */}
        <ArticleHeader post={post} />

        {/* Cover Image */}
        <div className="max-w-3xl mx-auto px-6 lg:px-0 mb-2">
          <div className="w-full h-72 lg:h-96 rounded-2xl overflow-hidden">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        {/* Article Body */}
        <ArticleBody content={post.content} tags={post.tags} faq={post.faq} />
      </div>

      {/* Related Posts */}
      <RelatedPosts posts={related} />

      <Footer />
    </main>
  );
}